import React, { useState, useRef } from 'react';
import { ArrowLeft, FileText, Truck, Mail, Sparkles, PauseCircle, Download, CheckCheck, FileSpreadsheet, X } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
interface Check {
  id: number;
  retailer: string;
  check: string;
  date: string;
  status: string;
  deduction: string;
  approved: number;
  rejected: number;
  pending: number;
}
interface CheckDetailProps {
  check: Check;
  onBack: () => void;
  onNavigate: (page: string) => void;
}
interface Claim {
  id: number;
  po: string;
  type: string;
  amount: string;
  status: string;
}
function IconTooltipButton({
  icon: Icon,
  label,
  color = 'text-slate-400 hover:text-slate-600',
  disabled = false,
  onClick
}: {
  icon: React.ElementType;
  label: string;
  color?: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  const ref = useRef<HTMLButtonElement>(null);
  const handleMouseEnter = () => {
    if (disabled) return;
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top + window.scrollY - 8,
        left: rect.left + rect.width / 2 + window.scrollX
      });
    }
    setVisible(true);
  };
  return <>
      <button ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)} disabled={disabled} className={`p-1.5 rounded-md transition-colors ${disabled ? 'text-slate-200 cursor-not-allowed' : color}`} onClick={e => {
      e.stopPropagation();
      if (!disabled && onClick) onClick();
    }} data-id="element-66">
        <Icon className="w-4 h-4" data-id="element-67" />
      </button>
      {visible && !disabled && <div className="fixed z-[9999] pointer-events-none" style={{
      top: pos.top,
      left: pos.left,
      transform: 'translate(-50%, -100%)'
    }} data-id="element-68">
          <div className="bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap mb-2" data-id="element-69">
            {label}
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-70" />
          </div>
        </div>}
    </>;
}
const claimsByCheck: Record<number, Claim[]> = {
  1: [{
    id: 1,
    po: 'PO-44821',
    type: 'Short Ship',
    amount: '$1,200',
    status: 'not-started'
  }, {
    id: 2,
    po: 'PO-44822',
    type: 'Price Discrepancy',
    amount: '$800',
    status: 'not-started'
  }, {
    id: 3,
    po: 'PO-44823',
    type: 'Short Ship',
    amount: '$2,100',
    status: 'not-started'
  }, {
    id: 4,
    po: 'PO-44824',
    type: 'Damaged Goods',
    amount: '$950',
    status: 'not-started'
  }, {
    id: 5,
    po: 'PO-44825',
    type: 'Short Ship',
    amount: '$3,400',
    status: 'not-started'
  }],
  2: [{
    id: 1,
    po: 'PO-88201',
    type: 'Short Ship',
    amount: '$600',
    status: 'completed'
  }, {
    id: 2,
    po: 'PO-88202',
    type: 'Price Discrepancy',
    amount: '$1,100',
    status: 'rejected'
  }, {
    id: 3,
    po: 'PO-88203',
    type: 'Damaged Goods',
    amount: '$400',
    status: 'awaiting-response'
  }, {
    id: 4,
    po: 'PO-88204',
    type: 'Short Ship',
    amount: '$850',
    status: 'completed'
  }, {
    id: 5,
    po: 'PO-88205',
    type: 'Allowance',
    amount: '$720',
    status: 'completed'
  }, {
    id: 6,
    po: 'PO-88206',
    type: 'Price Discrepancy',
    amount: '$930',
    status: 'completed'
  }, {
    id: 7,
    po: 'PO-88207',
    type: 'Short Ship',
    amount: '$1,050',
    status: 'completed'
  }, {
    id: 8,
    po: 'PO-88208',
    type: 'Damaged Goods',
    amount: '$680',
    status: 'completed'
  }, {
    id: 9,
    po: 'PO-88209',
    type: 'Allowance',
    amount: '$540',
    status: 'completed'
  }, {
    id: 10,
    po: 'PO-88210',
    type: 'Short Ship',
    amount: '$630',
    status: 'completed'
  }, {
    id: 11,
    po: 'PO-88211',
    type: 'Price Discrepancy',
    amount: '$780',
    status: 'completed'
  }, {
    id: 12,
    po: 'PO-88212',
    type: 'Short Ship',
    amount: '$910',
    status: 'completed'
  }, {
    id: 13,
    po: 'PO-88213',
    type: 'Damaged Goods',
    amount: '$470',
    status: 'rejected'
  }, {
    id: 14,
    po: 'PO-88214',
    type: 'Allowance',
    amount: '$1,200',
    status: 'rejected'
  }, {
    id: 15,
    po: 'PO-88215',
    type: 'Short Ship',
    amount: '$560',
    status: 'rejected'
  }, {
    id: 16,
    po: 'PO-88216',
    type: 'Price Discrepancy',
    amount: '$890',
    status: 'rejected'
  }, {
    id: 17,
    po: 'PO-88217',
    type: 'Short Ship',
    amount: '$340',
    status: 'rejected'
  }, {
    id: 18,
    po: 'PO-88218',
    type: 'Damaged Goods',
    amount: '$750',
    status: 'rejected'
  }, {
    id: 19,
    po: 'PO-88219',
    type: 'Allowance',
    amount: '$620',
    status: 'rejected'
  }, {
    id: 20,
    po: 'PO-88220',
    type: 'Short Ship',
    amount: '$480',
    status: 'awaiting-response'
  }, {
    id: 21,
    po: 'PO-88221',
    type: 'Price Discrepancy',
    amount: '$1,300',
    status: 'awaiting-response'
  }, {
    id: 22,
    po: 'PO-88222',
    type: 'Short Ship',
    amount: '$670',
    status: 'awaiting-response'
  }, {
    id: 23,
    po: 'PO-88223',
    type: 'Damaged Goods',
    amount: '$920',
    status: 'awaiting-response'
  }, {
    id: 24,
    po: 'PO-88224',
    type: 'Allowance',
    amount: '$510',
    status: 'awaiting-response'
  }, {
    id: 25,
    po: 'PO-88225',
    type: 'Short Ship',
    amount: '$840',
    status: 'awaiting-response'
  }, {
    id: 26,
    po: 'PO-88226',
    type: 'Price Discrepancy',
    amount: '$1,150',
    status: 'awaiting-response'
  }, {
    id: 27,
    po: 'PO-88227',
    type: 'Short Ship',
    amount: '$390',
    status: 'awaiting-response'
  }, {
    id: 28,
    po: 'PO-88228',
    type: 'Damaged Goods',
    amount: '$730',
    status: 'awaiting-response'
  }, {
    id: 29,
    po: 'PO-88229',
    type: 'Allowance',
    amount: '$580',
    status: 'awaiting-response'
  }, {
    id: 30,
    po: 'PO-88230',
    type: 'Short Ship',
    amount: '$960',
    status: 'awaiting-response'
  }, {
    id: 31,
    po: 'PO-88231',
    type: 'Price Discrepancy',
    amount: '$440',
    status: 'awaiting-response'
  }, {
    id: 32,
    po: 'PO-88232',
    type: 'Short Ship',
    amount: '$810',
    status: 'awaiting-response'
  }, {
    id: 33,
    po: 'PO-88233',
    type: 'Damaged Goods',
    amount: '$1,070',
    status: 'awaiting-response'
  }, {
    id: 34,
    po: 'PO-88234',
    type: 'Allowance',
    amount: '$650',
    status: 'awaiting-response'
  }, {
    id: 35,
    po: 'PO-88235',
    type: 'Short Ship',
    amount: '$520',
    status: 'awaiting-response'
  }, {
    id: 36,
    po: 'PO-88236',
    type: 'Price Discrepancy',
    amount: '$870',
    status: 'awaiting-response'
  }, {
    id: 37,
    po: 'PO-88237',
    type: 'Short Ship',
    amount: '$1,020',
    status: 'awaiting-response'
  }, {
    id: 38,
    po: 'PO-88238',
    type: 'Damaged Goods',
    amount: '$430',
    status: 'awaiting-response'
  }, {
    id: 39,
    po: 'PO-88239',
    type: 'Allowance',
    amount: '$760',
    status: 'awaiting-response'
  }, {
    id: 40,
    po: 'PO-88240',
    type: 'Short Ship',
    amount: '$590',
    status: 'awaiting-response'
  }],
  3: [{
    id: 1,
    po: 'PO-22190',
    type: 'Short Ship',
    amount: '$2,200',
    status: 'in-progress'
  }, {
    id: 2,
    po: 'PO-22191',
    type: 'Allowance',
    amount: '$1,800',
    status: 'in-progress'
  }, {
    id: 3,
    po: 'PO-22192',
    type: 'Price Discrepancy',
    amount: '$900',
    status: 'completed'
  }, {
    id: 4,
    po: 'PO-22193',
    type: 'Short Ship',
    amount: '$3,100',
    status: 'in-progress'
  }, {
    id: 5,
    po: 'PO-22194',
    type: 'Damaged Goods',
    amount: '$600',
    status: 'completed'
  }],
  4: [{
    id: 1,
    po: 'PO-11020',
    type: 'Short Ship',
    amount: '$1,500',
    status: 'completed'
  }, {
    id: 2,
    po: 'PO-11021',
    type: 'Price Discrepancy',
    amount: '$700',
    status: 'completed'
  }, {
    id: 3,
    po: 'PO-11022',
    type: 'Allowance',
    amount: '$2,000',
    status: 'completed'
  }, {
    id: 4,
    po: 'PO-11023',
    type: 'Short Ship',
    amount: '$850',
    status: 'completed'
  }, {
    id: 5,
    po: 'PO-11024',
    type: 'Damaged Goods',
    amount: '$600',
    status: 'completed'
  }, {
    id: 6,
    po: 'PO-11025',
    type: 'Price Discrepancy',
    amount: '$450',
    status: 'completed'
  }, {
    id: 7,
    po: 'PO-11026',
    type: 'Short Ship',
    amount: '$1,100',
    status: 'not-started'
  }, {
    id: 8,
    po: 'PO-11027',
    type: 'Allowance',
    amount: '$750',
    status: 'not-started'
  }],
  5: [{
    id: 1,
    po: 'PO-55910',
    type: 'Short Ship',
    amount: '$5,400',
    status: 'completed'
  }],
  6: [{
    id: 1,
    po: 'PO-33210',
    type: 'Short Ship',
    amount: '$4,200',
    status: 'not-started'
  }, {
    id: 2,
    po: 'PO-33211',
    type: 'Price Discrepancy',
    amount: '$3,100',
    status: 'not-started'
  }, {
    id: 3,
    po: 'PO-33212',
    type: 'Allowance',
    amount: '$2,800',
    status: 'not-started'
  }, {
    id: 4,
    po: 'PO-33213',
    type: 'Damaged Goods',
    amount: '$1,900',
    status: 'not-started'
  }, {
    id: 5,
    po: 'PO-33214',
    type: 'Short Ship',
    amount: '$1,600',
    status: 'not-started'
  }, {
    id: 6,
    po: 'PO-33215',
    type: 'Price Discrepancy',
    amount: '$1,600',
    status: 'not-started'
  }]
};
const statusLabel = (status: string) => status === 'not-started' ? 'Logistics Review' : status === 'in-progress' ? 'Processing' : status === 'awaiting-response' ? 'Awaiting Vendor Response' : status === 'rejected' ? 'Rejected' : status === 'completed' ? 'Approved' : status === 'finance-review' ? 'Finance Review' : status.charAt(0).toUpperCase() + status.slice(1);
export function CheckDetail({
  check,
  onBack,
  onNavigate
}: CheckDetailProps) {
  const claims = claimsByCheck[check.id] ?? [];
  const [showSpreadsheet, setShowSpreadsheet] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [showBOLModal, setShowBOLModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const checkSlug = check.check.replace('#', '');
  const defaultEmailBody = `Dear Logistics Team,

Could you please review the deductions in this check and confirm which ones you believe to be invalid deductions?

https://zenclaims.com/ashtel/${checkSlug}

Regards,
Lene`;
  const [emailBody, setEmailBody] = useState(defaultEmailBody);
  const spreadsheetBtnRef = useRef<HTMLButtonElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({
    top: 0,
    left: 0
  });
  const handleSpreadsheetHover = () => {
    if (spreadsheetBtnRef.current) {
      const rect = spreadsheetBtnRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top + window.scrollY - 8,
        left: rect.left + rect.width / 2 + window.scrollX
      });
    }
    setTooltipVisible(true);
  };
  const statusBadgeType = (status: string) => status === 'completed' ? 'success' : status === 'rejected' ? 'error' : status === 'awaiting-response' ? 'warning' : status === 'in-progress' ? 'processing' : status === 'not-started' ? 'neutral' : status === 'finance-review' ? 'info' : status === 'review' ? 'warning' : 'error';
  return <div className="space-y-6" data-id="element-71">
      {/* Back + Header */}
      <div data-id="element-72">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4" data-id="element-73">
          <ArrowLeft className="w-4 h-4" data-id="element-74" />
          Back to Checks
        </button>

        <div className="flex items-start justify-between" data-id="element-75">
          <div data-id="element-76">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-77">
              {check.retailer} — {check.check}
            </h2>
            <p className="text-slate-500 mt-1" data-id="element-78">
              Received {check.date} · Deduction {check.deduction}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-1" data-id="element-79">
            <StatusBadge status={statusBadgeType(check.status)} className="text-xs" data-id="element-80">
              {statusLabel(check.status)}
            </StatusBadge>
            {check.status === 'not-started' && <button onClick={() => {
            setEmailBody(defaultEmailBody);
            setShowReminderModal(true);
          }} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-81">
                <Mail className="w-3.5 h-3.5" data-id="element-82" />
                Send Reminder
              </button>}
            {check.status === 'in-progress' && <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-83">
                <PauseCircle className="w-3.5 h-3.5" data-id="element-84" />
                Pause
              </button>}
            {check.status === 'completed' && <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-85">
                <Download className="w-3.5 h-3.5" data-id="element-86" />
                Export
              </button>}
            {check.status === 'finance-review' && <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-87">
                <FileText className="w-3.5 h-3.5" data-id="element-88" />
                Send to Finance
              </button>}
            {check.status === 'review' && <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-89">
                <CheckCheck className="w-3.5 h-3.5" data-id="element-90" />
                Approve All
              </button>}
          </div>
        </div>

        {/* Summary pills */}
        <div className="flex items-center gap-4 mt-4" data-id="element-91">
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm" data-id="element-92">
            <span className="text-slate-500" data-id="element-93">Total Claims</span>
            <span className="ml-2 font-semibold text-slate-900" data-id="element-94">
              {check.approved + check.rejected + check.pending}
            </span>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2 text-sm" data-id="element-95">
            <span className="text-emerald-600" data-id="element-96">Approved</span>
            <span className="ml-2 font-semibold text-emerald-700" data-id="element-97">
              {check.approved}
            </span>
          </div>
          <div className="bg-rose-50 border border-rose-100 rounded-lg px-4 py-2 text-sm" data-id="element-98">
            <span className="text-rose-600" data-id="element-99">Rejected</span>
            <span className="ml-2 font-semibold text-rose-700" data-id="element-100">
              {check.rejected}
            </span>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2 text-sm" data-id="element-101">
            <span className="text-amber-600" data-id="element-102">Pending</span>
            <span className="ml-2 font-semibold text-amber-700" data-id="element-103">
              {check.pending}
            </span>
          </div>

          {/* Excel download button */}
          <button ref={spreadsheetBtnRef} onMouseEnter={handleSpreadsheetHover} onMouseLeave={() => setTooltipVisible(false)} onClick={() => setShowSpreadsheet(true)} className="p-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors" data-id="element-104">
            <FileSpreadsheet className="w-4 h-4" data-id="element-105" />
          </button>
          {tooltipVisible && <div className="fixed z-[9999] pointer-events-none" style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: 'translate(-50%, -100%)'
        }} data-id="element-106">
              <div className="bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap mb-2" data-id="element-107">
                Download Spreadsheet
                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-108" />
              </div>
            </div>}
        </div>
      </div>

      {/* Send Reminder Modal */}
      {showReminderModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-6" onClick={() => setShowReminderModal(false)} data-id="element-109">
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-110">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200" data-id="element-111">
              <div className="flex items-center gap-2" data-id="element-112">
                <Mail className="w-4 h-4 text-blue-600" data-id="element-113" />
                <span className="text-sm font-semibold text-slate-800" data-id="element-114">
                  Send Reminder Email
                </span>
              </div>
              <button onClick={() => setShowReminderModal(false)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-115">
                <X className="w-4 h-4" data-id="element-116" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-117">
              <div data-id="element-118">
                <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-119">
                  To
                </label>
                <input type="text" defaultValue="logistics@ashtelstudios.com" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-slate-50" data-id="element-120" />
              </div>
              <div data-id="element-121">
                <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-122">
                  Subject
                </label>
                <input type="text" defaultValue={`Deduction Review Request — ${check.check}`} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-slate-50" data-id="element-123" />
              </div>
              <div data-id="element-124">
                <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-125">
                  Body
                </label>
                <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={9} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white resize-none font-mono leading-relaxed" data-id="element-126" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100" data-id="element-127">
              <button onClick={() => setShowReminderModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors" data-id="element-128">
                Cancel
              </button>
              <button onClick={() => setShowReminderModal(false)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-129">
                <Mail className="w-3.5 h-3.5" data-id="element-130" />
                Send Email
              </button>
            </div>
          </div>
        </div>}

      {/* Spreadsheet modal */}
      {showSpreadsheet && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-6" onClick={() => setShowSpreadsheet(false)} data-id="element-131">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-id="element-132">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10" data-id="element-133">
              <div className="flex items-center gap-2" data-id="element-134">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" data-id="element-135" />
                <span className="text-sm font-semibold text-slate-800" data-id="element-136">
                  VEN335399-ONT_20260205
                </span>
              </div>
              <button onClick={() => setShowSpreadsheet(false)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-137">
                <X className="w-4 h-4" data-id="element-138" />
              </button>
            </div>
            <div className="p-4" data-id="element-139">
              <img src="https://cdn.magicpatterns.com/uploads/i1BHysRD6frvWGvLjxh4Ud/image.png" alt="Spreadsheet preview" className="w-full rounded-lg" data-id="element-140" />
            </div>
          </div>
        </div>}

      {/* BOL Modal */}
      {showBOLModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-6" onClick={() => setShowBOLModal(false)} data-id="element-141">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-id="element-142">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10" data-id="element-143">
              <div className="flex items-center gap-2" data-id="element-144">
                <Truck className="w-4 h-4 text-indigo-600" data-id="element-145" />
                <span className="text-sm font-semibold text-slate-800" data-id="element-146">
                  BOL from Logistics
                </span>
              </div>
              <button onClick={() => setShowBOLModal(false)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-147">
                <X className="w-4 h-4" data-id="element-148" />
              </button>
            </div>
            <div className="p-4" data-id="element-149">
              <img src="https://cdn.magicpatterns.com/uploads/j4WZMv7PvjeJ7gSCMvuxbs/image.png" alt="Bill of Lading" className="w-full rounded-lg" data-id="element-150" />
            </div>
          </div>
        </div>}

      {/* PO Modal */}
      {showPOModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-6" onClick={() => setShowPOModal(false)} data-id="element-151">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-id="element-152">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10" data-id="element-153">
              <div className="flex items-center gap-2" data-id="element-154">
                <FileText className="w-4 h-4 text-blue-600" data-id="element-155" />
                <span className="text-sm font-semibold text-slate-800" data-id="element-156">
                  PO from Retailer
                </span>
              </div>
              <button onClick={() => setShowPOModal(false)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-157">
                <X className="w-4 h-4" data-id="element-158" />
              </button>
            </div>
            <div className="p-4" data-id="element-159">
              <img src="https://cdn.magicpatterns.com/uploads/cP2f1qPYN6hkfy8N236ixr/image.png" alt="PO from retailer" className="w-full rounded-lg" data-id="element-160" />
            </div>
          </div>
        </div>}

      {/* Claims Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-161">
        <div className="p-4 border-b border-slate-100" data-id="element-162">
          <h3 className="font-semibold text-slate-900" data-id="element-163">Individual Claims</h3>
        </div>
        <div className="overflow-x-auto" data-id="element-164">
          <table className="w-full text-sm text-left" data-id="element-165">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-166">
              <tr data-id="element-167">
                <th className="px-6 py-3" data-id="element-168">PO #</th>
                <th className="px-6 py-3" data-id="element-169">Claim Type</th>
                <th className="px-6 py-3" data-id="element-170">Amount</th>
                <th className="px-6 py-3" data-id="element-171">Status</th>
                <th className="px-6 py-3" data-id="element-172">Artifacts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-173">
              {claims.map(claim => <tr key={claim.id} className="hover:bg-slate-50 transition-colors" data-id="element-174">
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-175">
                    {claim.po}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-176">{claim.type}</td>
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-177">
                    {claim.amount}
                  </td>
                  <td className="px-6 py-4" data-id="element-178">
                    <StatusBadge status={statusBadgeType(claim.status)} className="text-[10px]" data-id="element-179">
                      {statusLabel(claim.status)}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4" data-id="element-180">
                    <div className="flex items-center gap-1" data-id="element-181">
                      {(() => {
                    const isReviewOnly = claim.status === 'not-started' || claim.status === 'finance-review';
                    return <>
                            <IconTooltipButton icon={FileText} label="View PO from retailer" color="text-slate-400 hover:text-blue-600" onClick={() => setShowPOModal(true)} data-id="element-182" />
                            <IconTooltipButton icon={Truck} label="View BOL from logistics" color="text-slate-400 hover:text-indigo-600" disabled={isReviewOnly} onClick={() => setShowBOLModal(true)} data-id="element-183" />
                            <IconTooltipButton icon={Mail} label="View email thread" color="text-slate-400 hover:text-emerald-600" disabled={isReviewOnly} data-id="element-184" />
                            <IconTooltipButton icon={Sparkles} label="AI Assessment" color="text-slate-400 hover:text-violet-600" onClick={() => onNavigate('workbench')} data-id="element-185" />
                          </>;
                  })()}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}